// Receipt printing utility for Cerveceria Gibbons POS system
import thermalPrinterService from './thermalPrinterService';
import { getTaxRate, splitTaxInclusive, taxLabel } from './systemFeatures';
import { receiptPaymentLabel, tenderTipTotal } from './paymentMethods';

// Printer modes
export const PRINTER_MODES = {
    BROWSER: 'browser',
    THERMAL: 'thermal',
    AUTO: 'auto'
};

// Returns a plain text receipt for preview and printing
export const formatReceiptText = (orderItems, orderId, tableName, orderName, specialName = null, closedAt = null, payments = []) => {
    // Calculate totals
    // Note: IVA is already included in prices, so calculate backwards
    const total = orderItems.reduce((sum, item) => {
        const itemTotal = item.quantity * (Number(item.price || 0) - Number(item.discount || 0));
        return sum + itemTotal;
    }, 0);
    const taxRate = getTaxRate();
    const { subtotal, tax: ivaAmount } = splitTaxInclusive(total, taxRate);

    // Format date/time — use closedAt for closed orders, current time for active
    const now = closedAt ? new Date(closedAt) : new Date();
    const dateTime = now.toLocaleString('es-MX', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    });
    
    let receipt = '';
    receipt += '================================\n';
    receipt += '      CERVECERIA GIBBONS\n';
    receipt += '        C. ESCORZA 848\n';
    receipt += '        CGI181112777\n';
    receipt += '================================\n\n';
    receipt += `Fecha/Hora: ${dateTime}\n`;
    receipt += `Orden #: ${orderId}\n`;
    receipt += `Mesa: ${tableName || 'N/A'}\n`;
    if (orderName && orderName !== tableName) {
        receipt += `Nombre: ${orderName}\n`;
    }

    // Special/promotion section (if applied)
    if (specialName) {
        receipt += '\n***** PROMOCION APLICADA *****\n';
        receipt += `***** ${specialName} *****\n`;
    }
    receipt += '\n';
    receipt += '--------------------------------\n';
    receipt += 'CANT PRODUCTO             PRECIO\n';
    receipt += '--------------------------------\n';
    
    // Calculate total discount while building items list
    let totalDiscount = 0;
    orderItems.forEach(item => {
        const qty = item.quantity;
        const name = item.product_name;
        const price = Number(item.price || 0);
        const discount = Number(item.discount || 0);
        const itemTotal = qty * (price - discount);
        const qtyStr = qty.toString().padStart(2);
        const maxNameLength = 18;
        const truncatedName = name.length > maxNameLength
            ? name.substring(0, maxNameLength - 3) + '...'
            : name;
        const priceStr = `$${itemTotal.toFixed(2)}`.padStart(8);
        receipt += `${qtyStr}x ${truncatedName.padEnd(maxNameLength)}${priceStr}\n`;
        if (discount > 0) {
            // Display total discount for the line (per-unit discount * quantity)
            const lineDiscount = discount * qty;
            totalDiscount += lineDiscount;
            receipt += `    (Descuento: -$${lineDiscount.toFixed(2)})\n`;
        }
    });

    receipt += '--------------------------------\n';
    // Show total discount if there is any
    if (totalDiscount > 0) {
        receipt += `Descuento:` + ` -$${totalDiscount.toFixed(2)}`.padStart(22) + '\n\n';
    }
    receipt += `Subtotal:` + ` $${subtotal.toFixed(2)}`.padStart(23) + '\n';
    receipt += `${taxLabel(taxRate)}:` + ` $${ivaAmount.toFixed(2)}`.padStart(32 - taxLabel(taxRate).length - 1) + '\n';
    receipt += `TOTAL:` + ` $${total.toFixed(2)}`.padStart(26) + '\n';
    receipt += '================================\n';

    // How the check was paid, one line per tender
    if (payments.length > 0) {
        receipt += 'FORMA DE PAGO\n';
        payments.forEach(payment => {
            const label = receiptPaymentLabel(payment.payment_type_id);
            receipt += `${label}:` + ` $${Number(payment.amount).toFixed(2)}`.padStart(32 - label.length - 1) + '\n';
        });
        const tipTotal = tenderTipTotal(payments);
        if (tipTotal > 0) {
            receipt += `Propina:` + ` $${tipTotal.toFixed(2)}`.padStart(24) + '\n';
        }
        receipt += '================================\n';
    }

    receipt += '\n';
    receipt += '        Ahorremos el agua,\n';
    receipt += '         Tomemos cerveza\n';
    receipt += '\n';
    receipt += '            ¡Gracias!\n';
    receipt += '================================\n';
    return receipt;
};

// Print receipt with automatic printer detection
export const printReceipt = async (orderItems, orderId, tableName, orderName, specialName = null, mode = PRINTER_MODES.AUTO, closedAt = null, payments = []) => {
    try {
        // Auto mode: try thermal first, fallback to browser
        if (mode === PRINTER_MODES.AUTO || mode === PRINTER_MODES.THERMAL) {
            if (thermalPrinterService.isSupported() && thermalPrinterService.isConnected()) {
                await printThermalReceipt(orderItems, orderId, tableName, orderName, specialName, closedAt, payments);
                return { success: true, method: 'thermal' };
            } else if (mode === PRINTER_MODES.THERMAL) {
                throw new Error('Impresora térmica no está conectada');
            }
        }

        // Fallback to browser printing or explicit browser mode
        if (mode === PRINTER_MODES.AUTO || mode === PRINTER_MODES.BROWSER) {
            printBrowserReceipt(orderItems, orderId, tableName, orderName, specialName, closedAt, payments);
            return { success: true, method: 'browser' };
        }

        throw new Error('Modo de impresión no válido');
    } catch (error) {
        console.error('Error printing receipt:', error);

        // If thermal fails and we're in auto mode, fallback to browser
        if (mode === PRINTER_MODES.AUTO) {
            try {
                printBrowserReceipt(orderItems, orderId, tableName, orderName, specialName, closedAt, payments);
                return { success: true, method: 'browser', fallback: true };
            } catch (browserError) {
                throw new Error('Error en ambos métodos de impresión: ' + browserError.message);
            }
        }

        throw error;
    }
};

// Thermal printer function
export const printThermalReceipt = async (orderItems, orderId, tableName, orderName, specialName = null, closedAt = null, payments = []) => {
    if (!thermalPrinterService.isConnected()) {
        throw new Error('Impresora térmica no conectada. Use connectThermalPrinter() primero.');
    }

    try {
        await thermalPrinterService.printReceipt(orderItems, orderId, tableName, orderName, specialName, closedAt, payments);
        return true;
    } catch (error) {
        console.error('Error printing thermal receipt:', error);
        throw error;
    }
};

// Browser print function using iframe (works on iOS without popup blockers)
export const printBrowserReceipt = (orderItems, orderId, tableName, orderName, specialName = null, closedAt = null, payments = []) => {
    const receiptText = formatReceiptText(orderItems, orderId, tableName, orderName, specialName, closedAt, payments);

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.top = '-10000px';
    iframe.style.left = '-10000px';
    iframe.style.width = '400px';
    iframe.style.height = '0';
    iframe.style.border = 'none';

    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(`
        <html>
        <head>
            <style>
                body, pre {
                    margin: 0 !important;
                    padding: 8px !important;
                    font-family: 'Courier New', monospace;
                    font-size: 12px;
                    line-height: 1.2;
                }
                @media print {
                    body, pre {
                        margin: 0 !important;
                        padding: 4px !important;
                        font-size: 11px;
                    }
                }
            </style>
        </head>
        <body>
            <pre>${receiptText}</pre>
        </body>
        </html>
    `);
    iframeDoc.close();

    // Wait for content to render, then print
    setTimeout(() => {
        try {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
        } catch (err) {
            console.error('Iframe print error:', err);
        }
        // Clean up after print dialog
        setTimeout(() => {
            if (iframe.parentNode) {
                document.body.removeChild(iframe);
            }
        }, 1000);
    }, 300);
};

// Format item ticket as plain text (for browser fallback)
export const formatItemTicketText = (items, orderName, tableName, areaLabel, userName = '') => {
    const now = new Date();
    const time = now.toLocaleString('es-MX', {
        hour: '2-digit', minute: '2-digit', hour12: false
    });

    let ticket = '';
    ticket += '================================\n';
    ticket += `        ${areaLabel}\n`;
    ticket += '================================\n';
    ticket += `${time}`;
    if (tableName) ticket += `  Mesa: ${tableName}`;
    if (orderName && orderName !== tableName) ticket += `  ${orderName}`;
    ticket += '\n';
    if (userName) ticket += `Server: ${userName}\n`;
    ticket += '--------------------------------\n';

    items.forEach(item => {
        const qty = item.quantity || 1;
        let line = `${qty}x ${item.name}`;
        if (item.dontMake) line += ' [NO PREP]';
        ticket += line + '\n';
        if (item.note) {
            ticket += `   > ${item.note}\n`;
        }
    });

    ticket += '--------------------------------\n';
    return ticket;
};

// Print item ticket with automatic printer detection
export const printItemTicket = async (items, orderName, tableName, areaLabel, userName = '', mode = PRINTER_MODES.AUTO) => {
    try {
        if (mode === PRINTER_MODES.AUTO || mode === PRINTER_MODES.THERMAL) {
            if (thermalPrinterService.isSupported() && thermalPrinterService.isConnected()) {
                await thermalPrinterService.printItemTicket(items, orderName, tableName, areaLabel, userName);
                return { success: true, method: 'thermal' };
            } else if (mode === PRINTER_MODES.THERMAL) {
                throw new Error('Impresora térmica no está conectada');
            }
        }

        if (mode === PRINTER_MODES.AUTO || mode === PRINTER_MODES.BROWSER) {
            printBrowserItemTicket(items, orderName, tableName, areaLabel, userName);
            return { success: true, method: 'browser' };
        }

        throw new Error('Modo de impresión no válido');
    } catch (error) {
        console.error('Error printing item ticket:', error);
        if (mode === PRINTER_MODES.AUTO) {
            try {
                printBrowserItemTicket(items, orderName, tableName, areaLabel, userName);
                return { success: true, method: 'browser', fallback: true };
            } catch (browserError) {
                throw new Error('Error en ambos métodos de impresión: ' + browserError.message);
            }
        }
        throw error;
    }
};

// Browser print for item tickets
const printBrowserItemTicket = (items, orderName, tableName, areaLabel, userName = '') => {
    const ticketText = formatItemTicketText(items, orderName, tableName, areaLabel, userName);

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.top = '-10000px';
    iframe.style.left = '-10000px';
    iframe.style.width = '400px';
    iframe.style.height = '0';
    iframe.style.border = 'none';

    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
    iframeDoc.open();
    iframeDoc.write(`
        <html>
        <head>
            <style>
                body, pre {
                    margin: 0 !important;
                    padding: 8px !important;
                    font-family: 'Courier New', monospace;
                    font-size: 14px;
                    line-height: 1.4;
                    font-weight: bold;
                }
                @media print {
                    body, pre {
                        margin: 0 !important;
                        padding: 4px !important;
                        font-size: 13px;
                    }
                }
            </style>
        </head>
        <body>
            <pre>${ticketText}</pre>
        </body>
        </html>
    `);
    iframeDoc.close();

    setTimeout(() => {
        try {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
        } catch (err) {
            console.error('Iframe print error:', err);
        }
        setTimeout(() => {
            if (iframe.parentNode) {
                document.body.removeChild(iframe);
            }
        }, 1000);
    }, 300);
};

// Thermal printer management functions
export const connectThermalPrinter = async () => {
    try {
        await thermalPrinterService.connect();
        return { success: true, message: 'Impresora térmica conectada exitosamente' };
    } catch (error) {
        return { success: false, message: error.message };
    }
};

export const disconnectThermalPrinter = async () => {
    try {
        await thermalPrinterService.disconnect();
        return { success: true, message: 'Impresora térmica desconectada' };
    } catch (error) {
        return { success: false, message: error.message };
    }
};

export const isThermalPrinterSupported = () => {
    return thermalPrinterService.isSupported();
};

export const isThermalPrinterConnected = () => {
    return thermalPrinterService.isConnected();
};

export const testThermalPrinter = async () => {
    try {
        await thermalPrinterService.testPrint();
        return { success: true, message: 'Prueba de impresión completada' };
    } catch (error) {
        return { success: false, message: error.message };
    }
};

export const openCashDrawer = async () => {
    try {
        await thermalPrinterService.openCashDrawer();
        return { success: true, message: 'Cajón abierto' };
    } catch (error) {
        return { success: false, message: error.message };
    }
};

// Get printer status
export const getPrinterStatus = () => {
    return {
        supported: thermalPrinterService.isSupported(),
        connected: thermalPrinterService.isConnected(),
        browserSupported: typeof window !== 'undefined' && 'print' in window
    };
};